import React, { useEffect, useMemo, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  CartesianGrid,
  Cell,
} from "recharts";
import { useChartZoom } from "@/hooks/useChartZoom";
import { useAnalysisSync } from "@/components/analysis/AnalysisSyncContext";

const COLOR_POS = "#00C805";
const COLOR_NEG = "#FF3B30";
const COLOR_FLIP = "#FFD60A";
const COLOR_CURVE = "#007AFF";

const fmt = (n) => {
  if (n === undefined || n === null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1) return n.toFixed(2);
  return n.toFixed(3);
};

const ChartTooltip = ({ active, payload, label, spot }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div
      className="bg-[#0e0e10]/95 border border-white/10 rounded-md px-3 py-2 text-xs shadow-xl backdrop-blur-md"
      data-testid="gex-tooltip"
    >
      <div className="num text-white font-medium mb-1">Strike ${label}</div>
      <div className="num text-[11px] text-[#00C805]">
        Call GEX: {fmt(row.call_gex)}B
      </div>
      <div className="num text-[11px] text-[#FF3B30]">
        Put GEX: {fmt(row.put_gex)}B
      </div>
      <div className="num text-[11px] text-white/80 mt-1">
        Net: {fmt(row.net_gex)}B
      </div>
      {row.agg !== undefined && (
        <div className="num text-[11px] text-[#007AFF]">
          Aggregate: {fmt(row.agg)}B
        </div>
      )}
      <div className="text-[10px] text-white/40 mt-1">
        {(((+label - spot) / spot) * 100).toFixed(2)}% from spot
      </div>
    </div>
  );
};

const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-white/70" data-testid="gex-legend">
    <span className="flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLOR_POS }} /> Call GEX
    </span>
    <span className="flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLOR_NEG }} /> Put GEX
    </span>
    <span className="flex items-center gap-1.5">
      <span className="w-3 h-0.5" style={{ background: COLOR_CURVE }} /> Aggregate
    </span>
    <span className="flex items-center gap-1.5">
      <span className="w-3 h-0.5 border-t border-dashed" style={{ borderColor: COLOR_FLIP }} /> Gamma Flip
    </span>
  </div>
);

export const GEXChart = ({ data, loading }) => {
  const [expanded, setExpanded] = useState(false);
  // Strike-range view in % of spot: 2.5, 5, 10, 25 (All). Inline default 2.5%.
  // Default ±5% around spot — matches OptionStrat / SpotGamma convention.
  // ±2.5% (the old default) was too tight: only 4-7 strikes visible on most
  // equities, which made the chart look near-empty around the spot line.
  const [rangePct, setRangePct] = useState(0.05);
  // Compare: up to 2 selected strikes
  const [picks, setPicks] = useState([]);

  // Lock body scroll + ESC-to-close when fullscreen
  useEffect(() => {
    if (!expanded) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  // Reset picks whenever symbol/spot changes
  useEffect(() => {
    setPicks([]);
  }, [data?.symbol]);

  const merged = useMemo(() => {
    if (!data) return [];
    const aggByStrike = Object.fromEntries((data.aggregate_curve || []).map((p) => [p.strike, p.agg]));
    return (data.levels || []).map((lv) => ({
      ...lv,
      agg: aggByStrike[lv.strike] ?? 0,
    }));
  }, [data]);

  // Strike subset visible after range filter
  const visible = useMemo(() => {
    if (!data || !merged.length) return merged;
    const lo = data.spot * (1 - rangePct);
    const hi = data.spot * (1 + rangePct);
    return merged.filter((d) => d.strike >= lo && d.strike <= hi);
  }, [merged, data, rangePct]);

  const maxAbsNet = useMemo(() => {
    if (!visible.length) return 1;
    return Math.max(...visible.map((d) => Math.abs(d.net_gex))) * 1.15 || 1;
  }, [visible]);

  const maxAbsAgg = useMemo(() => {
    if (!visible.length) return 1;
    return Math.max(...visible.map((d) => Math.abs(d.agg))) * 1.15 || 1;
  }, [visible]);

  // ---- Compare picks ----
  const onBarClick = (datum) => {
    if (!datum || datum.strike == null) return;
    setPicks((prev) => {
      const exists = prev.find((p) => p.strike === datum.strike);
      if (exists) return prev.filter((p) => p.strike !== datum.strike);
      const next = [...prev, datum];
      return next.slice(-2); // keep last 2 max
    });
  };
  const clearPicks = () => setPicks([]);

  const comparison = useMemo(() => {
    if (picks.length !== 2 || !data) return null;
    const [a, b] = picks.slice().sort((p, q) => p.strike - q.strike);
    const strikeDelta = b.strike - a.strike;
    const netDelta = b.net_gex - a.net_gex;
    const aggDelta = b.agg - a.agg;
    const aPctFromSpot = ((a.strike - data.spot) / data.spot) * 100;
    const bPctFromSpot = ((b.strike - data.spot) / data.spot) * 100;
    const widthPct = (strikeDelta / data.spot) * 100;
    // Sum of net_gex in the band (inclusive)
    const inBand = merged.filter((d) => d.strike >= a.strike && d.strike <= b.strike);
    const bandNetSum = inBand.reduce((s, d) => s + d.net_gex, 0);
    return { a, b, strikeDelta, netDelta, aggDelta, aPctFromSpot, bPctFromSpot, widthPct, bandNetSum, count: inBand.length };
  }, [picks, merged, data]);

  // Zoom: numeric strike axis. Resets on symbol/range change. Must be
  // called before any early return to respect React Hook rules.
  const zoom = useChartZoom({
    data: visible,
    xKey: "strike",
    mode: "numeric",
    anchorValue: data?.spot,
    resetDeps: [data?.symbol, rangePct],
  });

  // Optional sync-zoom: when AnalysisSyncContext's syncEnabled is on and the
  // published priceWindow matches our symbol, override the strike domain.
  const sync = useAnalysisSync();
  useEffect(() => {
    if (!sync?.syncEnabled) return;
    const w = sync.priceWindow;
    if (!w || !data?.symbol) return;
    if (w.symbol && w.symbol !== data.symbol) return;
    if (w.lo != null && w.hi != null && w.hi > w.lo) {
      zoom.setXDomain([w.lo, w.hi]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sync?.syncEnabled, sync?.priceWindow, data?.symbol]);

  if (loading || !data) {
    return (
      <div className="h-[460px] flex items-center justify-center text-white/40 text-sm" data-testid="gex-chart-loading">
        Loading GEX surface…
      </div>
    );
  }

  // Inner chart body — reused inline and inside the fullscreen overlay.
  const ChartBody = ({ heightClass }) => (
    <div ref={zoom.wheelHostRef} className={`${heightClass} w-full`}>
      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} debounce={50}>
        <ComposedChart data={visible} margin={{ top: 10, right: 12, left: -10, bottom: 0 }} {...zoom.chartHandlers}>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis
            dataKey="strike"
            tickLine={false}
            axisLine={false}
            minTickGap={20}
            tick={{ fill: "rgba(255,255,255,0.5)" }}
            type="number"
            {...zoom.xAxisProps}
          />
          <YAxis
            yAxisId="bars"
            orientation="left"
            tickLine={false}
            axisLine={false}
            domain={[-maxAbsNet, maxAbsNet]}
            tickFormatter={(v) => `${v.toFixed(1)}B`}
            tick={{ fill: "rgba(255,255,255,0.5)" }}
          />
          <YAxis
            yAxisId="curve"
            orientation="right"
            tickLine={false}
            axisLine={false}
            domain={[-maxAbsAgg, maxAbsAgg]}
            tickFormatter={(v) => `${v.toFixed(1)}`}
            tick={{ fill: "rgba(0,122,255,0.6)" }}
          />
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltip spot={data.spot} />} />
          <ReferenceLine yAxisId="bars" y={0} stroke="rgba(255,255,255,0.25)" strokeWidth={1} />
          <ReferenceLine yAxisId="bars" x={data.gamma_flip} stroke={COLOR_FLIP} strokeDasharray="4 4"
            label={{ value: "Gamma Flip", position: "top", fill: COLOR_FLIP, fontSize: 10 }} />
          <ReferenceLine yAxisId="bars" x={data.spot} stroke="rgba(255,255,255,0.4)" strokeDasharray="2 4"
            label={{ value: "Spot", position: "insideTopLeft", fill: "rgba(255,255,255,0.7)", fontSize: 10 }} />
          <ReferenceLine yAxisId="bars" x={data.call_wall} stroke={COLOR_POS} strokeDasharray="3 3"
            label={{ value: "Call Wall", position: "top", fill: COLOR_POS, fontSize: 10 }} />
          <ReferenceLine yAxisId="bars" x={data.put_wall} stroke={COLOR_NEG} strokeDasharray="3 3"
            label={{ value: "Put Wall", position: "top", fill: COLOR_NEG, fontSize: 10 }} />
          {/* Highlight picked strikes */}
          {picks.map((p, i) => (
            <ReferenceLine
              key={`pick-${p.strike}`}
              yAxisId="bars"
              x={p.strike}
              stroke="#A98CFF"
              strokeWidth={2}
              strokeDasharray="3 2"
              label={{ value: i === 0 ? "A" : "B", position: "insideTopRight", fill: "#A98CFF", fontSize: 11 }}
            />
          ))}
          <Bar
            yAxisId="bars"
            dataKey="net_gex"
            barSize={10}
            radius={[2, 2, 2, 2]}
            isAnimationActive={false}
            onClick={(payload) => onBarClick(payload?.payload || payload)}
            cursor="pointer"
          >
            {visible.map((d, i) => {
              const isPicked = picks.some((p) => p.strike === d.strike);
              return (
                <Cell
                  key={`c-${i}`}
                  fill={isPicked ? "#A98CFF" : (d.net_gex >= 0 ? COLOR_POS : COLOR_NEG)}
                  fillOpacity={isPicked ? 1 : 0.85}
                  stroke={isPicked ? "#fff" : "none"}
                  strokeWidth={isPicked ? 1 : 0}
                />
              );
            })}
          </Bar>
          <Line yAxisId="curve" type="monotone" dataKey="agg" stroke={COLOR_CURVE} strokeWidth={2} dot={false}
            activeDot={{ r: 3, fill: COLOR_CURVE, stroke: "#fff", strokeWidth: 1 }} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );

  // Range chips
  const RANGES = [
    { pct: 0.025, label: "±2.5%" },
    { pct: 0.05, label: "±5%" },
    { pct: 0.10, label: "±10%" },
    { pct: 0.25, label: "±25% (All)" },
  ];
  const RangeChips = () => (
    <div className="flex items-center gap-1.5" data-testid="gex-range-chips">
      {RANGES.map((r) => (
        <button
          key={r.pct}
          type="button"
          onClick={() => setRangePct(r.pct)}
          data-testid={`gex-range-${r.pct}`}
          className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded border transition ${
            rangePct === r.pct
              ? "bg-white/[0.08] border-white/25 text-white"
              : "border-white/10 text-white/55 hover:text-white hover:border-white/25"
          }`}
        >
          {r.label}
        </button>
      ))}
    </div>
  );

  // Compare panel
  const ComparePanel = () => {
    if (!picks.length) {
      return (
        <div
          className="text-[11px] text-white/45 px-3 py-2 rounded-md border border-dashed border-white/10 bg-white/[0.02]"
          data-testid="gex-compare-empty"
        >
          Tap any two strikes to compare their GEX delta
        </div>
      );
    }
    if (picks.length === 1) {
      const p = picks[0];
      return (
        <div
          className="text-[11px] px-3 py-2 rounded-md border border-[#A98CFF]/35 bg-[#A98CFF]/[0.06] flex items-center justify-between gap-3"
          data-testid="gex-compare-partial"
        >
          <span className="text-white/85">
            Strike <span className="num text-white font-medium">${p.strike}</span> selected · pick one more
          </span>
          <button onClick={clearPicks} className="text-[10px] text-white/55 hover:text-white underline">
            Clear
          </button>
        </div>
      );
    }
    if (!comparison) return null;
    const sign = (n) => (n >= 0 ? "+" : "");
    return (
      <div
        className="rounded-md border border-[#A98CFF]/35 bg-[#A98CFF]/[0.06] p-3"
        data-testid="gex-compare-panel"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#A98CFF]">
            Strike comparison · A → B
          </div>
          <button
            onClick={clearPicks}
            data-testid="gex-compare-clear"
            className="text-[10px] text-white/55 hover:text-white underline"
          >
            Clear
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
          <div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-white/45">A · {comparison.aPctFromSpot >= 0 ? "ITM call" : "ITM put"} side</div>
            <div className="num text-white font-medium mt-0.5">${comparison.a.strike}</div>
            <div className="num text-[10px] text-white/55">{sign(comparison.aPctFromSpot)}{comparison.aPctFromSpot.toFixed(2)}% spot</div>
            <div className="num text-[10px] text-white/55">net {sign(comparison.a.net_gex)}{comparison.a.net_gex.toFixed(2)}B</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-white/45">B</div>
            <div className="num text-white font-medium mt-0.5">${comparison.b.strike}</div>
            <div className="num text-[10px] text-white/55">{sign(comparison.bPctFromSpot)}{comparison.bPctFromSpot.toFixed(2)}% spot</div>
            <div className="num text-[10px] text-white/55">net {sign(comparison.b.net_gex)}{comparison.b.net_gex.toFixed(2)}B</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-white/45">Δ Strike</div>
            <div className="num text-white font-medium mt-0.5">{comparison.strikeDelta.toFixed(2)} pts</div>
            <div className="num text-[10px] text-white/55">band {comparison.widthPct.toFixed(2)}%</div>
            <div className="num text-[10px] text-white/55">{comparison.count} strikes</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-white/45">Δ Net GEX</div>
            <div
              className="num font-medium mt-0.5"
              style={{ color: comparison.netDelta >= 0 ? COLOR_POS : COLOR_NEG }}
            >
              {sign(comparison.netDelta)}{comparison.netDelta.toFixed(3)}B
            </div>
            <div className="num text-[10px] text-white/55">band sum {sign(comparison.bandNetSum)}{comparison.bandNetSum.toFixed(2)}B</div>
            <div className="num text-[10px]" style={{ color: comparison.aggDelta >= 0 ? COLOR_POS : COLOR_NEG }}>
              agg Δ {sign(comparison.aggDelta)}{comparison.aggDelta.toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const HeaderRow = ({ inOverlay = false }) => (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
      <Legend />
      <div className="flex items-center gap-3 sm:gap-4 text-[11px] uppercase tracking-[0.18em] text-white/40">
        <span data-testid="gex-spot">Spot <span className="num text-white normal-case tracking-normal">${data.spot}</span></span>
        <span data-testid="gex-flip-label">Flip <span className="num normal-case tracking-normal" style={{ color: COLOR_FLIP }}>${data.gamma_flip}</span></span>
        {sync && (
          <button
            type="button"
            onClick={() => sync.setSyncEnabled(!sync.syncEnabled)}
            data-testid="gex-sync-toggle"
            title="Sync strike range with chart price window"
            className={`text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border normal-case tracking-normal transition ${
              sync.syncEnabled
                ? "border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]"
                : "border-white/15 text-white/45 hover:text-white"
            }`}
          >
            ⇄ Sync
          </button>
        )}
        <div className="normal-case tracking-normal">
          <zoom.Controls compact anchor={data.spot} />
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!inOverlay)}
          data-testid={inOverlay ? "gex-chart-collapse" : "gex-chart-expand"}
          aria-label={inOverlay ? "Collapse chart" : "Expand chart"}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 hover:border-white/30 text-white/70 hover:text-white transition normal-case tracking-normal"
          title={inOverlay ? "Collapse (Esc)" : "Expand chart"}
        >
          {inOverlay ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="w-full" data-testid="gex-chart-container">
        <HeaderRow />
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <RangeChips />
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/35 num">
            {visible.length} strikes shown · {merged.length} total
          </span>
        </div>
        <ChartBody heightClass="h-[420px] sm:h-[460px] min-h-[420px]" />
        <div className="mt-3">
          <ComparePanel />
        </div>
      </div>

      {expanded && (
        <div
          className="fixed inset-0 z-[80] bg-[#050505]/95 backdrop-blur-md p-3 sm:p-6 flex flex-col"
          data-testid="gex-chart-fullscreen"
          onClick={(e) => { if (e.target === e.currentTarget) setExpanded(false); }}
        >
          <div
            className="flex-1 w-full max-w-[1600px] mx-auto bg-[#0a0a0c] border border-white/10 rounded-xl p-4 sm:p-6 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                  Gamma exposure · expanded view
                </div>
                <div className="text-base sm:text-lg font-medium text-white mt-0.5">
                  Visible ${visible[0]?.strike ?? "—"}–${visible[visible.length - 1]?.strike ?? "—"}
                  <span className="text-[12px] text-white/40 num ml-2 font-normal">
                    of ${merged[0]?.strike ?? "—"}–${merged[merged.length - 1]?.strike ?? "—"} full range
                  </span>
                </div>
              </div>
              <RangeChips />
            </div>
            <HeaderRow inOverlay />
            <div className="flex-1 min-h-0">
              <ChartBody heightClass="h-full min-h-[400px]" />
            </div>
            <div className="mt-3">
              <ComparePanel />
            </div>
            <div className="text-[10px] text-white/40 mt-3 text-center">
              Press <span className="text-white/70">Esc</span> or tap outside to close
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GEXChart;
